class IngredientsCarouselWithProducts extends SliderComponent {
  constructor() {
    super();

    try {
      this._slides = Array.from(this.querySelectorAll('.featured-ingredients__slide'));
      this._infoBlocks = Array.from(this.querySelectorAll('.info-body__block'));
      this._addonBlocks = Array.from(this.querySelectorAll('.featured-ingredients__additional-info'));
      this._accordionBlocks = Array.from(this.querySelectorAll('.featured-ingredient__accordion-item'));

      this._titleEl = this.querySelector('.list-item__title');
      this._labelWrap = this.querySelector('.list-item__label-wrapper');
      this._labelEl = this.querySelector('.list-item__label');

      const sectionId = this.getAttribute('data-section-id');
      this._liveRegion = sectionId ? document.getElementById(`fi-live-${sectionId}`) : null;

      this._infoByIndex = new Map(this._infoBlocks.map(n => [String(n.dataset.infoIndex), n]));
      this._addonByIndex = new Map(this._addonBlocks.map(n => [String(n.dataset.additionalInfoIndex), n]));
      this._accByIndex   = new Map(this._accordionBlocks.map(n => [String(n.dataset.accordionIndex), n]));
      this._slideByIndex = new Map(this._slides.map(n => [String(n.dataset.slideIndex), n]));

      this._active = { info: null, addon: null, acc: null, slide: null, index: "1" };

      this._pendingIndex = null;
      this._raf = null;
      this._lastSlideOffset = 0;
    } catch (error) {
      console.error('Error initializing Ingredients Carousel:', error);
    }
  }

  connectedCallback() {
    this.slider = this.querySelector('[id^="Slider-"]');
    if (this.slider) {
      this.slider.scrollLeft = 0;
    }

    super.connectedCallback();

    try {
      this._firstSlideChange = true;

      const firstSlide = this._slides && this._slides.length > 0 ? this._slides[0] : null;
      const startIndex = firstSlide ? String(firstSlide.dataset.slideIndex) : "1";

      if (this._slides && this._slides.length > 0) {
        this._slides.forEach(slide => {
          slide.classList.remove('active-slide');
          slide.setAttribute('aria-current', 'false');
        });
        if (firstSlide) {
          firstSlide.classList.add('active-slide');
          firstSlide.setAttribute('aria-current', 'true');
        }
      }

      this._active.index = null;
      this._applyIndex(startIndex);

      this.addEventListener('slideChanged', this._onSlideChanged, { passive: true });
    } catch (error) {
      console.error('Error in Ingredients Carousel connectedCallback:', error);
    }
  }

  _onSlideChanged = (event) => {
    if (!event?.detail) return;

    const el = event.detail.currentElement;
    let idx;

    if (el && el.dataset.slideIndex) {
      idx = String(el.dataset.slideIndex);
    } else if (event.detail.currentPage) {
      idx = String(event.detail.currentPage);
    } else {
      idx = this._active.index;
    }

    if (this._firstSlideChange) {
      this._firstSlideChange = false;
      if (idx !== "1" && this._slides && this._slides.length > 0) {
        const firstSlide = this._slides[0];
        if (firstSlide) {
          idx = String(firstSlide.dataset.slideIndex || "1");
          this._slides.forEach(slide => {
            slide.classList.remove('active-slide');
            slide.setAttribute('aria-current', 'false');
          });
          firstSlide.classList.add('active-slide');
          firstSlide.setAttribute('aria-current', 'true');
        }
      }
    }

    this._pendingIndex = idx;

    if (!this._raf) {
      this._raf = requestAnimationFrame(() => {
        const indexToApply = this._pendingIndex;
        this._raf = null;
        this._applyIndex(indexToApply);
      });
    } else {
      if (this._raf) {
        cancelAnimationFrame(this._raf);
      }
      this._raf = requestAnimationFrame(() => {
        const indexToApply = this._pendingIndex;
        this._raf = null;
        this._applyIndex(indexToApply);
      });
    }
  };

  _applyIndex(indexStr) {
    if (!indexStr || indexStr === this._active.index) return;

    const nextInfo  = this._infoByIndex.get(indexStr);
    const nextAddon = this._addonByIndex.get(indexStr);
    const nextAcc   = this._accByIndex.get(indexStr);
    const nextSlide = this._slideByIndex.get(indexStr) || this._slides[Number(indexStr) - 1];

    this._infoBlocks.forEach(n => { if (n !== nextInfo) this._setHidden(n, true); });
    this._addonBlocks.forEach(n => { if (n !== nextAddon) this._setHidden(n, true); });
    this._accordionBlocks.forEach(n => { if (n !== nextAcc) this._setHidden(n, true); });

    if (nextSlide && this._titleEl) {
      const title = nextSlide.dataset.title || '';
      const label = nextSlide.dataset.label || '';

      this._titleEl.textContent = title;
      if (this._liveRegion) this._liveRegion.textContent = title;

      if (this._labelEl && this._labelWrap) {
        if (label) {
          this._labelWrap.hidden = false;
          this._labelEl.textContent = label;
        } else {
          this._labelWrap.hidden = true;
        }
      }
    }

    this._setHidden(this._active.info, true);
    this._setHidden(nextInfo, false);

    this._setHidden(this._active.addon, true);
    this._setHidden(nextAddon, false);

    this._setHidden(this._active.acc, true);
    this._setHidden(nextAcc, false);

    if (this._active.slide) {
      this._active.slide.classList.remove('active-slide');
      this._active.slide.setAttribute('aria-current', 'false');
    }
    if (nextSlide) {
      nextSlide.classList.add('active-slide');
      nextSlide.setAttribute('aria-current', 'true');
    }

    this._active = { info: nextInfo || null, addon: nextAddon || null, acc: nextAcc || null, slide: nextSlide || null, index: indexStr };
  }

  _setHidden(node, shouldHide) {
    if (!node) return;
    if (shouldHide) {
      if (!node.hidden) node.hidden = true;
      node.setAttribute('aria-hidden', 'true');
    } else {
      if (node.hidden) node.hidden = false;
      node.removeAttribute('aria-hidden');
    }
  }

  isSlideVisible(element, offset = 0) {
    if (!this.sliderItemsToShow || !this.sliderItemsToShow.length) return super.isSlideVisible(element, offset);
    const lastVisible = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
    if (lastVisible) {
      if (this._lastSlideOffset === 0) {
        const mr = window.getComputedStyle(lastVisible).marginRight;
        this._lastSlideOffset = parseInt(mr, 10) || 0;
      }
      if (this._lastSlideOffset > 0) offset = this._lastSlideOffset;
    }
    return super.isSlideVisible(element, offset);
  }
}

try {
  customElements.define('ingredients-carousel-with-products', IngredientsCarouselWithProducts);
} catch (error) {
  console.error('Error registering Ingredients Carousel component:', error);
}
